import React from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { Loading } from '@winnin/winnin_ui.components.loading'

import { useTranslate } from 'lib/tolgee'
import { humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'

import MiniCards from '../MiniCards'
import './index.scss'

const { MiniCardDefault } = MiniCards
const TOLGEE_PREFIX = 'MetricsCard'

const handleViewsValue = (views, info) => {
  if (info?.hasViewsReaction) {
    return humanizeLargeNumber(views, 1)
  }
  return ' - '
}

const handleCommentsValue = (comments, info) => {
  if (info?.hasCommentsReaction) {
    return humanizeLargeNumber(comments, 1)
  }
  return ' - '
}

const handleLikesValue = (likes, info) => {
  if (!info?.hasLikesReaction) {
    return ' - '
  }
  if (info?.isLikesReactionDisabled) {
    return <Icon icon="eyeClosed" fill="var(--base_color1)" width={32} height={32} />
  }
  return humanizeLargeNumber(likes, 1)
}

function MetricsCard({ metrics, provider, loading, className }) {
  const t = useTranslate()

  if (!metrics) {
    return null
  }

  const { comments, views, likes, live, maxConcurrentViewers } = metrics
  const showDash = value => (Number(value) === 0 ? ' - ' : value)
  const emptyMaxConcurrentViewers = live?.state === 'finished' && live?.performance?.length === 0

  function getExplanationToLikes() {
    if (provider === 'tw') {
      return t({ ...TOLGEE_GLOBALS.metricNotApplicableToPlatform, parameters: { provider } })
    }
    if (metrics?.info?.isLikesReactionDisabled) {
      return t(TOLGEE_GLOBALS.infoHiddenByCreator)
    }
  }

  return (
    <div className={classNames('video-about-card', className, { 'live-metric': live })}>
      <div className="content">
        <div className="info-content">
          {!loading ? (
            <>
              {live && (
                <MiniCardDefault
                  title={t({
                    key: `${TOLGEE_PREFIX}-labelMaxViewer`,
                    defaultValue: 'Max. number of simultaneous viewers',
                  })}
                  maxViewers
                  explanation={
                    provider === 'yt' &&
                    t({
                      key: `${TOLGEE_PREFIX}-youtubeExplanation`,
                      defaultValue:
                        'The maximum number of simultaneous viewers in a live can be higher than the number showed on Winnin Intelligence as we collect data by taking snapshots of moments.',
                    })
                  }
                  sideTooltip="bottom"
                  value={!emptyMaxConcurrentViewers && showDash(humanizeLargeNumber(maxConcurrentViewers, 1))}
                  subtitle={
                    emptyMaxConcurrentViewers &&
                    t({
                      key: `${TOLGEE_PREFIX}-emptyViewers`,
                      defaultValue: "We can't collect the number of viewers at this time.",
                    })
                  }
                />
              )}

              <MiniCardDefault
                subtitle={t({ key: `${TOLGEE_PREFIX}-labelAllTime`, defaultValue: 'All time' })}
                title={t({ key: `${TOLGEE_PREFIX}-labelViews`, defaultValue: 'Views' })}
                value={handleViewsValue(views, metrics?.info)}
              />

              <MiniCardDefault
                explanation={
                  provider === 'tw' && t({ ...TOLGEE_GLOBALS.metricNotApplicableToPlatform, parameters: { provider } })
                }
                subtitle={t({ key: `${TOLGEE_PREFIX}-labelAllTime`, defaultValue: 'All time' })}
                title={t({ key: `${TOLGEE_PREFIX}-labelComments`, defaultValue: 'Comments' })}
                value={handleCommentsValue(comments, metrics?.info)}
              />

              <MiniCardDefault
                explanation={getExplanationToLikes()}
                subtitle={t({ key: `${TOLGEE_PREFIX}-labelAllTime`, defaultValue: 'All time' })}
                title={t({ key: `${TOLGEE_PREFIX}-labelLikes`, defaultValue: 'Likes' })}
                value={handleLikesValue(likes, metrics?.info)}
              />
            </>
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </div>
  )
}

export default observer(MetricsCard)
